/**
 * Pipedrive Files Page
 * List of files with search and pagination
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { usePipedriveFiles } from '@/hooks/use-pipedrive-api'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { FileText, Search, ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react'
import type { PipedriveFile } from '@/types/pipedrive'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'

export default function PipedriveFiles() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const limit = 25

  const { data, isLoading, error } = usePipedriveFiles({
    page,
    limit,
    search,
  })

  const files = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.pages ?? 1

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  // GRK-BRAND-6: file-type badge styling from categorical palette
  // `.tint` fill + Prussian text (WCAG AA). Matches RepositoryAssetThumbnail + Content.tsx
  const getFileTypeStyle = (type: string | null): React.CSSProperties => {
    const fam = (f: keyof typeof CATEGORICAL) => ({
      backgroundColor: CATEGORICAL[f].tint,
      color: GRACKLE_COLORS.prussianBlue,
    })

    const lower = type?.toLowerCase() ?? ''
    if (lower === 'pdf') return fam('raspberry')
    if (['png', 'jpg', 'jpeg', 'gif'].includes(lower)) return fam('navy')
    if (['doc', 'docx'].includes(lower)) return fam('steel')
    if (['xls', 'xlsx', 'csv'].includes(lower)) return fam('evergreen')
    if (['ppt', 'pptx'].includes(lower)) return fam('clay')
    if (lower === 'html') return fam('teal')
    if (lower === 'txt') return fam('slate')
    if (lower === 'zip') return fam('slate')
    return fam('slate') // fallback
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Files</h1>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load files. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" aria-hidden="true" />
            Files
          </h1>
          <p className="text-muted-foreground">{total.toLocaleString()} files</p>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search files..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9"
                  aria-label="Search files"
                />
              </div>
            </div>
            {search && (
              <Button variant="outline" onClick={() => handleSearch('')}>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Uploaded By</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Deal</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-6 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  </TableRow>
                ))
              ) : files.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No files found
                  </TableCell>
                </TableRow>
              ) : (
                files.map((file: PipedriveFile) => (
                  <TableRow key={file.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        {file.download_url ? (
                          <a
                            href={file.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            {file.file_name || 'Unnamed file'}
                            <Download className="h-3 w-3" />
                          </a>
                        ) : (
                          file.file_name || 'Unnamed file'
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge style={getFileTypeStyle(file.file_type)} variant="secondary">
                        {file.file_type?.toUpperCase() || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>{file.user_name || '-'}</TableCell>
                    <TableCell>
                      {file.organization_id ? (
                        <Link
                          to={`/pipedrive/organizations/${file.organization_id}`}
                          className="text-primary hover:underline"
                        >
                          {file.organization || '-'}
                        </Link>
                      ) : (
                        file.organization || '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {file.deal_id ? (
                        <Link
                          to={`/pipedrive/deals/${file.deal_id}`}
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          {file.deal || '-'}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        file.deal || '-'
                      )}
                    </TableCell>
                    <TableCell>{file.add_time || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
            {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
